import {Injectable} from '@angular/core';
import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import {defer, Observable, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay, switchMap, take, tap} from 'rxjs/operators';
import {LoginService} from '../service/login.service';
import {MessageService} from 'primeng/api';
import {LoaderRequestService} from '../service/loader-request.service';
import {Router} from '@angular/router';
import { Store } from '@ngxs/store';
import { SetAccessToken } from '@app/state/sesion/sesion.actions';

// Clave por defecto de NgxsStoragePluginModule.forRoot() (app.module.ts), donde persiste todo el estado.
const NGXS_STORAGE_KEY = '@@STATE';
const MISMATCH_RELOAD_KEY = 'mismatchReloadAt';
const MISMATCH_RELOAD_WINDOW_MS = 30 * 1000;

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private refreshToken$: Observable<string> | null = null;

  constructor(
    public auth: LoginService,
    private messageService: MessageService,
    private loaderService: LoaderRequestService,
    private router: Router,
    private store: Store
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return defer(() => {
      const authenticatedRequest = this.auth.getToken() ? this.addTokenToRequest(request) : request;
      this.showLoader(authenticatedRequest);

      return defer(() => next.handle(authenticatedRequest)).pipe(
        catchError((errorResponse: HttpErrorResponse) => {
          if (errorResponse.status === 401 && this.isSessionAccountMismatch(errorResponse)) {
            this.refreshCurrentTab();
            return throwError(() => errorResponse);
          }

          // Sin sesión activa (ej. 401 de un request en vuelo tras el logout) no se renueva el token.
          if (errorResponse.status === 401 && this.auth.getLogin() && !request.url.includes('login')
              && !request.headers.get('skipAuthRefresh')) {
            return this.handle401Error(authenticatedRequest, next);
          }

          this.handleErrorMessages(errorResponse, authenticatedRequest);
          return throwError(() => errorResponse);
        }),
        // También se ejecuta al cancelar y después del reintento por renovación del token.
        finalize(() => this.hideLoader(authenticatedRequest))
      );
    });
  }

  private addTokenToRequest(request: HttpRequest<any>, token: string | null = null): HttpRequest<any> {
    const authToken = token ? `Bearer ${token}` : this.auth.getToken();
    return request.clone({
      setHeaders: {
        Authorization: authToken,
        companyInfo: this.auth.getCompanyInfo()
      }
    });
  }

  private isSessionAccountMismatch(errorResponse: HttpErrorResponse): boolean {
    return errorResponse.error && errorResponse.error.code === 'SESSION_ACCOUNT_MISMATCH';
  }

  private refreshCurrentTab(): void {
    this.refreshToken$ = null;
    // Otra pestaña inició sesión con otra cuenta: al recargar, esta pestaña toma esa sesión desde localStorage.
    // Si el token persistido es el mismo que el de memoria, recargar no cambia nada y la app entraría en un
    // bucle infinito de recargas (el mismatch se repite en cada arranque); en ese caso se cierra la sesión.
    if (this.hayOtraSesionPersistida() && !this.recargoRecientePorMismatch()) {
      this.marcarRecargaPorMismatch();
      this.reloadPage();
      return;
    }
    this.router.navigateByUrl('/logout');
  }

  // Separado para poder espiarlo en tests: window.location.reload no se puede mockear en Chrome.
  private reloadPage(): void {
    window.location.reload();
  }

  private hayOtraSesionPersistida(): boolean {
    try {
      const state = JSON.parse(localStorage.getItem(NGXS_STORAGE_KEY) || '{}');
      const tokenPersistido = state?.sesion?.login?.accessToken;
      const tokenEnMemoria = this.auth.getLogin()?.accessToken;
      return !!tokenPersistido && tokenPersistido !== tokenEnMemoria;
    } catch {
      return false;
    }
  }

  // Red de seguridad: como máximo una recarga por mismatch cada MISMATCH_RELOAD_WINDOW_MS por pestaña.
  private recargoRecientePorMismatch(): boolean {
    try {
      const ultimaRecarga = Number(sessionStorage.getItem(MISMATCH_RELOAD_KEY) || 0);
      return Date.now() - ultimaRecarga < MISMATCH_RELOAD_WINDOW_MS;
    } catch {
      return false;
    }
  }

  private marcarRecargaPorMismatch(): void {
    try {
      sessionStorage.setItem(MISMATCH_RELOAD_KEY, String(Date.now()));
    } catch {
      // sessionStorage no disponible: se recarga igual, hayOtraSesionPersistida ya evita el bucle.
    }
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.refreshToken$) {
      this.refreshToken$ = defer(() => this.auth.refresh()).pipe(
        take(1),
        map((tokens: {accessToken: string}) => tokens.accessToken),
        tap(token => this.store.dispatch(new SetAccessToken(token))),
        catchError((errorResponse: HttpErrorResponse) => {
          if (this.isSessionAccountMismatch(errorResponse)) {
            this.refreshCurrentTab();
          } else {
            this.router.navigateByUrl('/logout');
          }
          return throwError(() => errorResponse);
        }),
        finalize(() => this.refreshToken$ = null),
        // Todos los 401 reciben el token o el error. Cancelar un GET no interrumpe la renovación compartida.
        shareReplay({bufferSize: 1, refCount: false})
      );
    }

    return this.refreshToken$.pipe(
      switchMap(token => next.handle(this.addTokenToRequest(request, token)))
    );
  }

  private handleErrorMessages(errorResponse: HttpErrorResponse, request: HttpRequest<any>): void {
    // Si la petición tiene el header hideErrorMessage, no mostramos el mensaje
    const hideErrorMessage = request.headers.get('hideErrorMessage');

    if (hideErrorMessage) {
      return; // No mostrar mensaje de error
    }

    if (errorResponse && errorResponse.error && errorResponse.error.mensaje) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: errorResponse.error.mensaje });
    } else {
      if (errorResponse.error) {
        if (errorResponse.error.message) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: errorResponse.error.message });
        }
        if (errorResponse.error.error === 'Unauthorized') {
          this.router.navigateByUrl('/logout');
        }
      }
    }
  }

  private showLoader(request: HttpRequest<any>): void {
    const background = request.method === 'GET';
    const hideLoader = request.headers.get('hideLoader');
    const isPrinting = request.headers.get('isPrinting');
    if (!hideLoader) {
      this.loaderService.call({
        show: true,
        background
      });
    }
    if (isPrinting) {
      this.loaderService.call({
        show: true,
        background: true
      });
    }
  }

  private hideLoader(request: HttpRequest<any>): void {
    const background = request.method === 'GET';
    const hideLoader = request.headers.get('hideLoader');
    const isPrinting = request.headers.get('isPrinting');
    if (!hideLoader) {
      this.loaderService.call({
        show: false,
        background
      });
    }
    if (isPrinting) {
      this.loaderService.call({
        show: false,
        background: true
      });
    }
  }
}
